import { Link } from "react-router-dom";
import { useConsent } from "@/contexts/ConsentContext";

export default function CookiePolicy() {
    const { openPreferences } = useConsent();
    return (
        <div className="ok-container py-12 max-w-3xl mx-auto" data-testid="cookie-policy-page">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Cookies</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-2">Cookie policy</h1>
            <p className="text-xs opacity-60 mt-1">Last updated: 13 June 2026</p>

            <div className="prose prose-invert mt-8 text-sm leading-relaxed space-y-6 max-w-none">
                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">What are cookies?</h2>
                    <p>
                        Cookies are small text files stored on your device. They let the site remember you are logged in,
                        keep your theme and consent preferences, and (only with your consent) measure how the archive is used.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Categories we use</h2>
                    <div className="space-y-4">
                        <div className="ok-card p-4">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Essential · Always on</div>
                            <h3 className="font-display text-lg mt-1">Login, security &amp; preferences</h3>
                            <p className="text-sm opacity-80 mt-1">
                                Authentication session cookie ("token"), CSRF / security tokens, theme preference, your cookie
                                consent choice. Cannot be disabled — the archive cannot function without them.
                            </p>
                        </div>
                        <div className="ok-card p-4">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Analytics · Opt-in</div>
                            <h3 className="font-display text-lg mt-1">Google Analytics 4</h3>
                            <p className="text-sm opacity-80 mt-1">
                                Provider: Google Ireland Ltd. Cookies: _ga, _ga_*, _gid. Purpose: aggregated page-view and
                                conversion measurement. Retention: up to 14 months. Loaded only after analytics consent.
                            </p>
                        </div>
                        <div className="ok-card p-4">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Advertising · Opt-in</div>
                            <h3 className="font-display text-lg mt-1">Meta Pixel (Facebook)</h3>
                            <p className="text-sm opacity-80 mt-1">
                                Provider: Meta Platforms Ireland Ltd. Cookies: _fbp, _fbc. Purpose: ad campaign measurement,
                                conversion attribution, audience matching for Meta ads. Retention: up to 90 days. Loaded only
                                after advertising consent.
                            </p>
                        </div>
                        <div className="ok-card p-4">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Advertising · Opt-in</div>
                            <h3 className="font-display text-lg mt-1">TikTok Pixel</h3>
                            <p className="text-sm opacity-80 mt-1">
                                Provider: TikTok Information Technologies UK Ltd. Cookies: _ttp, ttwid. Purpose: ad campaign
                                measurement and conversion attribution for TikTok ads. Retention: up to 13 months. Loaded only
                                after advertising consent.
                            </p>
                        </div>
                    </div>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Default behaviour</h2>
                    <p>
                        All non-essential cookies are <b>denied by default</b> for every visitor. We use Google Consent Mode v2
                        so GA4 will not store anything until you explicitly grant analytics consent. Meta and TikTok scripts are
                        not even loaded until you grant advertising consent.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Manage your choices</h2>
                    <p>
                        You can review and change your consent at any time:
                    </p>
                    <button
                        onClick={openPreferences}
                        className="ok-btn-primary mt-3"
                        data-testid="cookie-policy-open-prefs"
                    >
                        Manage cookie preferences
                    </button>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Withdrawing consent</h2>
                    <p>
                        Withdrawing consent stops new tracking immediately. To delete historical analytics data tied to your
                        browser, use the "Reject non-essential" option then clear your browser cookies for projectunveiled.com.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl mt-6 mb-2">Questions</h2>
                    <p>
                        Read our <Link to="/privacy" className="underline">privacy policy</Link> or reach us via the{" "}
                        <Link to="/contact" className="underline">contact form</Link>.
                    </p>
                </section>
            </div>
        </div>
    );
}
